import { useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { getStroke } from 'perfect-freehand';
import { distance, type Point } from '../../doc/geometry';
import type { Geometry } from '../../doc/schema';
import { commitTransient } from '../actions';
import { expandGroups } from '../arrange';
import { useBoard, usePane, usePaneStore, useReadOnly } from '../context';
import { filterSelectable, isInk, lassoStrokes, strokeAt, strokeHit } from '../ink';
import { outlineToPath, strokeOptions } from '../items/StrokeItem';
import { toolSettings } from '../toolSettings';
import { screenToWorld, worldToScreen } from './camera';

/** Time after a pen touch during which finger input is treated as a resting palm. */
export const PALM_WINDOW_MS = 1500;
let lastPenContact = 0;
export const notePenContact = (): void => {
  lastPenContact = Date.now();
};
export const penRecentlyUsed = (): boolean => Date.now() - lastPenContact < PALM_WINDOW_MS;

/** Should this pointer be ignored as a palm (or per the pen-only setting)? */
export function isRejectedTouch(pointerType: string): boolean {
  if (pointerType === 'pen') {
    notePenContact();
    return false;
  }
  if (pointerType !== 'touch') return false;
  return toolSettings.getState().penOnly || penRecentlyUsed();
}

/** Screen pixels within which a press counts as "on" a stroke (lasso tap and move). */
const HIT_PX = 8;
const TAP_PX = 4;

/**
 * Screen-space canvas that captures ink while the draw tool is active. The stroke is drawn
 * immediately on the canvas (no React on the hot path); on release it becomes a stroke item on
 * the ink layer of its tool. In lasso mode the same canvas draws the selection loop, selects
 * the strokes it touches, and moves the selection when a selected stroke is dragged.
 * Pointers it does not take (middle button, space, rejected touches) fall through to the
 * viewport, which pans.
 */
export function DrawOverlay() {
  const active = usePane((s) => s.tool === 'draw');
  const readOnly = useReadOnly();
  if (!active || readOnly) return null;
  return <DrawCanvas />;
}

type Live =
  | { kind: 'ink'; pointerId: number; /** World-space points with pressure. */ points: [number, number, number][]; realPressure: boolean }
  | { kind: 'erase'; pointerId: number; last: Point; erased: Set<string> }
  | { kind: 'lasso'; pointerId: number; start: Point; /** Flat world coords of the loop. */ ring: number[]; additive: boolean; dragged: boolean }
  | { kind: 'move'; pointerId: number; start: Point; startWorld: Point; hitId: string; additive: boolean; origins: Map<string, Geometry>; dragged: boolean };

function DrawCanvas() {
  const board = useBoard();
  const pane = usePaneStore();
  const size = usePane((s) => s.size);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const live = useRef<Live | null>(null);
  /** Whether the live gesture was started by a finger (a second finger then cancels it). */
  const liveTouch = useRef(false);
  const dpr = window.devicePixelRatio || 1;

  // Strokes made before ink layers existed (or left behind by a deleted layer) get their layer now.
  useEffect(() => board.adoptStrokes(), [board]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    c.width = Math.round(size.w * dpr);
    c.height = Math.round(size.h * dpr);
  }, [size, dpr]);

  // Safari still runs its touch defaults (text selection, callouts, synthesised clicks on the
  // UI underneath) for pointers we ignore. Cancel them at the touch level, non-passively.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const swallow = (e: TouchEvent) => {
      if (e.cancelable) e.preventDefault();
    };
    const opts: AddEventListenerOptions = { passive: false };
    c.addEventListener('touchstart', swallow, opts);
    c.addEventListener('touchmove', swallow, opts);
    c.addEventListener('touchend', swallow, opts);
    return () => {
      c.removeEventListener('touchstart', swallow);
      c.removeEventListener('touchmove', swallow);
      c.removeEventListener('touchend', swallow);
    };
  }, []);

  const toLocal = (e: { clientX: number; clientY: number }): Point => {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const render = () => {
    const c = canvasRef.current;
    const l = live.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size.w, size.h);
    if (!l) return;
    const ts = toolSettings.getState();
    const cam = pane.getState().camera;
    if (l.kind === 'erase') {
      const p = worldToScreen(cam, l.last);
      ctx.beginPath();
      ctx.arc(p.x, p.y, (ts.eraserSize / 2) * cam.zoom, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(180,40,40,0.9)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      return;
    }
    if (l.kind === 'lasso') {
      if (l.ring.length < 4) return;
      const accent = getComputedStyle(c).getPropertyValue('--accent').trim() || '#2b5bd7';
      ctx.beginPath();
      for (let i = 0; i < l.ring.length; i += 2) {
        const p = worldToScreen(cam, { x: l.ring[i]!, y: l.ring[i + 1]! });
        if (i === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      }
      ctx.closePath();
      ctx.fillStyle = accent;
      ctx.globalAlpha = 0.12;
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.strokeStyle = accent;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 3]);
      ctx.stroke();
      ctx.setLineDash([]);
      return;
    }
    if (l.kind !== 'ink' || !l.points.length) return;
    const tool = ts.penMode === 'highlighter' ? 'highlighter' : 'pen';
    const sizeWorld = (tool === 'pen' ? ts.penSize : ts.highlighterSize) / cam.zoom;
    const screenPts = l.points.map(([x, y, p]) => {
      const s = worldToScreen(cam, { x, y });
      return [s.x, s.y, p] as [number, number, number];
    });
    const outline = getStroke(screenPts, strokeOptions(sizeWorld * cam.zoom, tool, l.realPressure));
    const path = new Path2D(outlineToPath(outline));
    ctx.globalAlpha = tool === 'highlighter' ? 0.45 : 1;
    ctx.fillStyle = tool === 'pen' ? ts.penColor : ts.highlighterColor;
    ctx.fill(path);
    ctx.globalAlpha = 1;
  };

  const eraseAt = (world: Point, radiusWorld: number, erased: Set<string>) => {
    const hits = board.index.query({ x: world.x - radiusWorld, y: world.y - radiusWorld, w: radiusWorld * 2, h: radiusWorld * 2 });
    const toDelete: string[] = [];
    for (const item of hits) {
      if (!isInk(item) || board.isLocked(item) || board.isHidden(item) || erased.has(item.id)) continue;
      if (strokeHit(item, world, radiusWorld)) toDelete.push(item.id);
    }
    if (toDelete.length) {
      for (const id of toDelete) erased.add(id);
      board.deleteItems(toDelete);
    }
  };

  /** A selected stroke under the world point, if any (the handle for moving the selection). */
  const selectedStrokeAt = (world: Point, zoom: number): string | null => {
    const r = HIT_PX / zoom;
    const s = pane.getState();
    let best: string | null = null;
    for (const id of s.selection) {
      const item = board.index.get(id);
      if (item && isInk(item) && !board.isLocked(item) && strokeHit(item, world, r)) best = id; // last = topmost in z among selected? close enough
    }
    return best;
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const l = live.current;
    if (l) {
      // A second finger beside a drawing finger makes a gesture (two-finger tap = undo, pinch),
      // not ink: drop the stroke in progress instead of committing a dot the gesture would then
      // undo. The new pointer falls through to the viewport.
      if (e.pointerType === 'touch' && liveTouch.current) {
        live.current = null;
        if (e.currentTarget.hasPointerCapture(l.pointerId)) e.currentTarget.releasePointerCapture(l.pointerId);
        if (l.kind === 'move') pane.getState().clearTransient();
        render();
      }
      return;
    }
    const s = pane.getState();
    if (e.button !== 0 && e.pointerType !== 'touch') return; // middle/right: fall through (pan)
    if (s.spaceHeld) return;
    if (isRejectedTouch(e.pointerType)) return; // palm or pen-only: viewport handles (two-finger pan)
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    liveTouch.current = e.pointerType === 'touch';
    const local = toLocal(e);
    const world = screenToWorld(s.camera, local);
    const mode = toolSettings.getState().penMode;
    if (mode === 'eraser') {
      live.current = { kind: 'erase', pointerId: e.pointerId, last: world, erased: new Set() };
      eraseAt(world, toolSettings.getState().eraserSize / 2 / s.camera.zoom, live.current.erased);
    } else if (mode === 'lasso') {
      const additive = e.shiftKey;
      const hitId = selectedStrokeAt(world, s.camera.zoom);
      if (hitId) {
        const origins = new Map<string, Geometry>();
        for (const id of s.selection) {
          const item = board.index.get(id);
          if (item && !board.isLocked(item)) origins.set(id, item);
        }
        live.current = { kind: 'move', pointerId: e.pointerId, start: local, startWorld: world, hitId, additive, origins, dragged: false };
      } else {
        live.current = { kind: 'lasso', pointerId: e.pointerId, start: local, ring: [world.x, world.y], additive, dragged: false };
      }
    } else {
      const realPressure = e.pointerType === 'pen';
      live.current = { kind: 'ink', pointerId: e.pointerId, points: [[world.x, world.y, realPressure ? e.pressure : 0.5]], realPressure };
    }
    render();
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const l = live.current;
    const s = pane.getState();
    if (!l) {
      // idle in lasso mode: show that a selected stroke can be dragged
      if (e.pointerType !== 'touch' && toolSettings.getState().penMode === 'lasso' && s.selection.size) {
        const over = selectedStrokeAt(screenToWorld(s.camera, toLocal(e)), s.camera.zoom);
        e.currentTarget.style.cursor = over ? 'move' : '';
      }
      return;
    }
    if (l.pointerId !== e.pointerId) return;
    const native = e.nativeEvent;
    const events = typeof native.getCoalescedEvents === 'function' ? native.getCoalescedEvents() : [];
    const list = events.length ? events : [native];
    if (l.kind === 'move') {
      const local = toLocal(e);
      if (!l.dragged && distance(local, l.start) < TAP_PX) return;
      l.dragged = true;
      const world = screenToWorld(s.camera, local);
      const dx = world.x - l.startWorld.x;
      const dy = world.y - l.startWorld.y;
      const t: Record<string, Partial<Geometry>> = {};
      for (const [id, o] of l.origins) t[id] = { x: o.x + dx, y: o.y + dy };
      s.setTransient(t);
      return;
    }
    for (const ev of list) {
      const local = toLocal(ev);
      const world = screenToWorld(s.camera, local);
      if (l.kind === 'erase') {
        l.last = world;
        eraseAt(world, toolSettings.getState().eraserSize / 2 / s.camera.zoom, l.erased);
      } else if (l.kind === 'lasso') {
        if (!l.dragged && distance(local, l.start) >= TAP_PX) l.dragged = true;
        const n = l.ring.length;
        const lastScreen = worldToScreen(s.camera, { x: l.ring[n - 2]!, y: l.ring[n - 1]! });
        if (distance(lastScreen, local) < 2) continue; // keep the loop light: the hit test is O(loop x stroke)
        l.ring.push(world.x, world.y);
      } else {
        const last = l.points[l.points.length - 1]!;
        if (distance({ x: last[0], y: last[1] }, world) * s.camera.zoom < 0.7) continue;
        l.points.push([world.x, world.y, l.realPressure ? ev.pressure : 0.5]);
      }
    }
    render();
  };

  const finish = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const l = live.current;
    if (!l || l.pointerId !== e.pointerId) return;
    live.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
    render();
    const s = pane.getState();
    const cam = s.camera;
    if (l.kind === 'erase') return;
    if (l.kind === 'move') {
      if (l.dragged) {
        commitTransient({ board, pane });
      } else if (l.additive) {
        s.select([...s.selection].filter((id) => id !== l.hitId));
      } else {
        s.select([l.hitId]);
      }
      return;
    }
    if (l.kind === 'lasso') {
      const ctx = { board, pane };
      if (!l.dragged) {
        // a tap: pick the stroke under it, or clear
        const hit = strokeAt(board, screenToWorld(cam, toLocal(e)), HIT_PX / cam.zoom);
        const members = hit ? filterSelectable(board, expandGroups(ctx, [hit.id]), 'draw') : [];
        if (l.additive) s.addToSelection(members);
        else s.select(members);
        return;
      }
      const hits = filterSelectable(board, expandGroups(ctx, lassoStrokes(board, l.ring)), 'draw');
      if (l.additive) s.addToSelection(hits);
      else s.select(hits);
      return;
    }
    const ts = toolSettings.getState();
    const tool = ts.penMode === 'highlighter' ? 'highlighter' : 'pen';
    const sizeWorld = (tool === 'pen' ? ts.penSize : ts.highlighterSize) / cam.zoom;
    const pad = sizeWorld;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const [x, y] of l.points) {
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
    const x = minX - pad;
    const y = minY - pad;
    const w = Math.max(1, maxX - minX + pad * 2);
    const h = Math.max(1, maxY - minY + pad * 2);
    const flat: number[] = [];
    for (const [px, py, p] of l.points) flat.push(round(px - x), round(py - y), round(p));
    board.createItem({
      type: 'stroke',
      x: round(x),
      y: round(y),
      w: round(w),
      h: round(h),
      props: {
        tool,
        color: tool === 'pen' ? ts.penColor : ts.highlighterColor,
        size: round(sizeWorld),
        points: flat,
        w0: round(w),
        h0: round(h),
        pressure: l.realPressure,
      },
    });
  };

  return (
    <canvas
      ref={canvasRef}
      className="draw-overlay"
      style={{ width: size.w, height: size.h }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finish}
      onPointerCancel={finish}
    />
  );
}

const round = (v: number) => Math.round(v * 100) / 100;
